import * as React from "react";
import Image from "next/image";
import { EventVendorQuery } from "@src/@types/api.d";
import Icon from "@src/components/Icon";
import { decodeHtmlEntities } from "@src/utils/general";

import defaultImage from "../../../../public/images/image20.jpeg";
import bgImg from "../../../../public/images/t19bg.png";

type Props = {
  eventId: number;
  eventVendorData: EventVendorQuery;
  sectionImage?: string;
};

interface VendorCardProps {
  vendor: any;
  index: number;
}

const Vendors: React.FC<Props> = ({
  eventId,
  eventVendorData,
  sectionImage,
}) => {
  const VendorCard: React.FC<VendorCardProps> = ({ vendor, index }) => {
    const decodedName = decodeHtmlEntities(vendor?.name);
    const decodedRole = decodeHtmlEntities(vendor?.role);
    const decodedEmail = decodeHtmlEntities(vendor?.email);
    const decodedInstagramHandle = decodeHtmlEntities(vendor?.instagram_handle);

    return (
      <div className="p-4 flex flex-col justify-center items-center text-center text-[#FCE2B0] gap-2 lg:gap-4 max-w-xs w-full">
        <div className="font-ebGaramond text-xl font-semibold tracking-wider uppercase">
          {decodedName}
        </div>
        <p className="font-ebGaramond text-lg text-[#FFF3DB]">{decodedRole}</p>
        <p className="font-ebGaramond text-sm text-[#FFF3DB]">{decodedEmail}</p>
        <div className="flex gap-2 items-center mt-2">
          {!!decodedInstagramHandle && (
            <a
              target="_blank"
              href={
                decodedInstagramHandle.includes("@")
                  ? `https://www.instagram.com/${
                      decodedInstagramHandle.split("@")[1]
                    }/`
                  : `https://www.instagram.com/${decodedInstagramHandle}/`
              }
              rel="noopener noreferrer"
              className="text-[#FCE2B0] hover:text-[#FFF3DB] transition-colors"
            >
              <Icon iconName="instagramWhite" />
            </a>
          )}
        </div>
      </div>
    );
  };

  return eventVendorData?.eventVendor &&
    eventVendorData?.eventVendor.length !== 0 ? (
    <div className="w-full flex flex-col lg:flex-row lg:min-h-[100vh]">
      {/* Content Section - Takes natural height on mobile, minimum viewport height but can expand on desktop */}
      <div className="w-full lg:flex-1 relative order-1 lg:order-2">
        {/* Background image */}
        <div className="absolute inset-0">
          <Image
            src={bgImg}
            alt="background"
            layout="fill"
            className="object-cover object-center"
          />
        </div>

        <div className="relative z-10 w-full flex flex-col justify-center items-center py-8 lg:py-16 px-6 lg:px-10 lg:min-h-[100vh]">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-ntFab text-[#FCE2B0] mb-8 lg:mb-12">
            Our Vendors
          </h1>
          <div className="flex flex-wrap justify-center gap-6 lg:gap-8 max-w-4xl w-full">
            {eventVendorData?.eventVendor?.map((vendor, index) => (
              <VendorCard
                key={`vendors-home-page-${index}`}
                vendor={vendor}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Image Section - Fixed height on mobile, follows content */}
      <div className="w-full h-[50vh] lg:flex-1 lg:min-h-[100vh] order-2 lg:order-1 lg:hidden">
        <div className="relative w-full h-full">
          <Image
            loading="eager"
            layout="fill"
            src={sectionImage || defaultImage}
            alt="vendors-image"
            className="object-cover object-center lg:object-top"
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

export default Vendors;
